import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Banner } from '@/components/feedback/Banner'
import { Button } from '@/components/ui/Button'
import { useSession } from '@/features/auth/api/useSession'
import { CAP, can } from '@/lib/can'
import { cn } from '@/lib/cn'
import { formatMoneyString } from '@/lib/format'
import type { CaseMoney } from '@/types/domain'

/* The two notices at the top of the money card (S-12, direction A, locked
 * visual decisions 1–3).
 *
 * Both are SERVED facts, and this component decides neither:
 *
 *  · `doubleCount` — legs whose per-unit plans neither detail a plan line nor
 *    stand alone, beside top-level plan lines nobody details. One SQL predicate
 *    on the server decides it (finance); the plan lines are resolved here by id
 *    against `money.expenses` of the same response, for their NAMES only.
 *  · `unallocatedOutbound` — outbound payments with an unallocated remainder.
 *    The amounts are the server's per-currency sums, printed digit for digit
 *    and never added together here (AD-9).
 *
 * Neither moves a figure (AD-11; visual decision 3): the balances, the profit
 * line and the forecast render exactly as they would without them. Both are
 * omitted by the server once the case's status no longer accepts money, so a
 * closed case shows neither. Neither is dismissible: each disappears when the
 * thing it names is fixed (the leg answered, the payment allocated). */
export function MoneyNotices({
  caseId,
  money,
  onAllocate,
  className,
}: {
  caseId: string
  money: CaseMoney
  /** Brings «Платежи поставщикам» into view — where an unallocated
   *  remainder carries its «Разнести» (S-11 revision). */
  onAllocate: () => void
  /** The host's spacing. Inside a card the notices take its gutter; on
   *  «Затраты» they stand between the tiles and the cost lines. */
  className?: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: session } = useSession()

  const doubleCount = money.doubleCount
  const firstLeg = doubleCount?.legIds[0]
  const unallocated = money.unallocatedOutbound

  /* Mounted by `MoneySection` only when the block carries either key, so a
     case with nothing to say mounts no router hook at all. */
  if (!firstLeg && !unallocated) return null

  /* Names, never figures: the client sums no money. An id the expenses list
     does not hold (a response older than the line) is skipped rather than
     printed as a uuid. */
  const planLines = (doubleCount?.planLineIds ?? []).flatMap((id) => {
    const line = money.expenses.find((expense) => expense.id === id)
    return line ? [t('cases.legs.vehiclesTable.quotedLine', { line: line.description })] : []
  })

  /* Allocation is Finance's act (`Allocate` needs `CanRecordPayments`), gated
     here on the capability and never on a role name. Shown to everybody else
     too, refused with the reason: the note is about the case they are reading,
     and the act it points at is somebody else's. */
  const mayAllocate = can(session, CAP.financePaymentRecord)

  return (
    <div className={cn('flex flex-col gap-2.5 px-[18px] pt-3.5', className)}>
      {firstLeg ? (
        <Banner
          tone="attention"
          title={
            planLines.length > 0
              ? t('cases.money.notices.doubleCountTitle', {
                  count: planLines.length,
                  lines: planLines.join(', '),
                })
              : t('cases.money.notices.doubleCountTitleUnnamed')
          }
          action={
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void navigate(`/cases/${caseId}/legs/${firstLeg}`)}
            >
              {t('cases.money.notices.openLeg')}
            </Button>
          }
        >
          {t('cases.money.notices.doubleCountBody')}
          {(doubleCount?.legIds.length ?? 0) > 1
            ? ` ${t('cases.money.notices.doubleCountLegs', { count: doubleCount?.legIds.length })}`
            : null}
        </Banner>
      ) : null}

      {unallocated ? (
        <Banner
          tone="info"
          title={t('cases.money.notices.unallocatedTitle', {
            amounts: unallocated.amounts
              .map((sum) => formatMoneyString(sum.amount, sum.currency))
              .join(' · '),
          })}
          action={
            <Button
              variant="secondary"
              size="sm"
              blockedReason={
                mayAllocate ? undefined : t('cases.money.notices.allocateBlocked')
              }
              onClick={onAllocate}
            >
              {t('cases.money.notices.allocate')}
            </Button>
          }
        />
      ) : null}
    </div>
  )
}
